import { Link, useLocation } from 'react-router-dom'
import { useEffect, useState } from 'react'
import type { AuthUser } from '@/lib/hooks/useAuth'

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:3001'

async function fetchAvatarBlob(avatarUrl: string, token: string): Promise<string | null> {
  const [pId, fname] = avatarUrl.split('/')
  try {
    const r = await fetch(`${API}/api/providers/avatar/${pId}/${fname}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!r.ok) return null
    return URL.createObjectURL(await r.blob())
  } catch { return null }
}

const navItems = [
  {
    href: '/missions-disponibles',
    label: 'Missions disponibles',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
      </svg>
    ),
    exact: false,
    excludes: [] as string[],
  },
  {
    href: '/mes-missions',
    label: 'Mes missions',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
      </svg>
    ),
    exact: false,
    excludes: [] as string[],
  },
  {
    href: '/qualifications',
    label: 'Qualifications',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
      </svg>
    ),
    exact: false,
    excludes: [] as string[],
  },
  {
    href: '/disponibilites',
    label: 'Mes disponibilités',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" x2="16" y1="2" y2="6"/><line x1="8" x2="8" y1="2" y2="6"/><line x1="3" x2="21" y1="10" y2="10"/><path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01M16 18h.01"/>
      </svg>
    ),
    exact: false,
    excludes: [] as string[],
  },
  {
    href: '/profil-prestataire',
    label: 'Mon profil',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="8" r="5"/><path d="M20 21a8 8 0 1 0-16 0"/>
      </svg>
    ),
    exact: true,
    excludes: [] as string[],
  },
  {
    href: '/guide',
    label: 'Guide d\'utilisation',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/>
      </svg>
    ),
    exact: true,
    excludes: [] as string[],
  },
] as const

interface ProviderSidebarProps {
  user: AuthUser
  onLogout: () => void
}

export function ProviderSidebar({ user, onLogout }: ProviderSidebarProps) {
  const pathname = useLocation().pathname
  const [profileComplete, setProfileComplete] = useState<boolean | null>(null)
  const [avatarBlobUrl, setAvatarBlobUrl] = useState<string | null>(null)
  const [initials, setInitials] = useState<string>('')
  const [unreadChat, setUnreadChat] = useState(0)

  useEffect(() => {
    const token = localStorage.getItem('velink_token')
    if (!token) return

    async function fetchUnread() {
      try {
        const r = await fetch(`${API}/api/missions/messages/unread-count`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (r.ok) {
          const { count } = await r.json()
          setUnreadChat(count)
        }
      } catch { /* silencieux */ }
    }
    fetchUnread()
    const t = setInterval(fetchUnread, 10000)
    return () => clearInterval(t)
  }, [])

  useEffect(() => {
    const token = localStorage.getItem('velink_token')
    if (!token) return

    fetch(`${API}/api/providers/me/completion`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (data) setProfileComplete(data.isComplete) })
      .catch(() => {})

    fetch(`${API}/api/providers/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : null)
      .then(async (data) => {
        if (!data) return
        setInitials(`${data.firstName?.[0] ?? ''}${data.lastName?.[0] ?? ''}`)
        if (data.avatarUrl) {
          const url = await fetchAvatarBlob(data.avatarUrl, token)
          if (url) setAvatarBlobUrl(url)
        }
      })
      .catch(() => {})
  }, [pathname])

  function isActive(item: (typeof navItems)[number]) {
    if (item.excludes.some((e) => pathname.startsWith(e))) return false
    if (item.exact) return pathname === item.href
    return pathname.startsWith(item.href)
  }

  return (
    <aside className="fixed left-0 top-0 bottom-0 w-60 flex flex-col z-40 border-r border-white/[0.08] bg-[rgba(10,10,20,0.75)] backdrop-blur-2xl">
      {/* Logo */}
      <div className="px-5 pt-6 pb-5 border-b border-white/[0.08]">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-success/20 border border-success/30 flex items-center justify-center flex-shrink-0">
            <span className="text-success font-bold">V</span>
          </div>
          <div>
            <p className="text-text-primary font-semibold text-sm leading-tight">Velink</p>
            <p className="text-text-muted text-xs">Espace prestataire</p>
          </div>
        </div>
      </div>

      {/* Badge profil incomplet */}
      {profileComplete === false && (
        <div className="px-3 pt-3">
          <Link
            to="/qualifications"
            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[#F59E0B]/10 border border-[#F59E0B]/30 hover:bg-[#F59E0B]/20 transition-all group"
          >
            <span className="text-[#F59E0B] text-sm">⚠️</span>
            <div className="min-w-0">
              <p className="text-[#F59E0B] text-xs font-semibold leading-tight">Profil incomplet</p>
              <p className="text-[#F59E0B]/60 text-[10px] truncate">Documents manquants →</p>
            </div>
          </Link>
        </div>
      )}

      {/* Retour au site */}
      <div className="px-3 pt-3">
        <Link
          to="/"
          className="flex items-center gap-2 px-3 py-2 rounded-xl bg-accent/10 border border-accent/25 hover:bg-accent/20 transition-all text-accent font-semibold text-sm group"
        >
          <span className="group-hover:-translate-x-0.5 transition-transform">←</span>
          Retour au site
        </Link>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const active = isActive(item)
          const showProfileBadge = item.href === '/qualifications' && profileComplete === false
          const showChatBadge = item.href === '/mes-missions' && unreadChat > 0
          return (
            <Link
              key={item.href}
              to={item.href}
              className={[
                'flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 border',
                active
                  ? 'bg-white/[0.10] border-white/[0.12] text-text-primary'
                  : 'text-text-secondary hover:bg-white/[0.06] hover:text-text-primary border-transparent',
              ].join(' ')}
            >
              <span className="flex-shrink-0 relative">
                {item.icon}
                {showChatBadge && (
                  <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-accent text-white text-[9px] font-bold flex items-center justify-center">
                    {unreadChat > 9 ? '9+' : unreadChat}
                  </span>
                )}
              </span>
              {item.label}
              {showProfileBadge && (
                <span className="ml-auto w-2 h-2 rounded-full bg-[#F59E0B] flex-shrink-0" />
              )}
              {showChatBadge && (
                <span className="ml-auto px-1.5 py-0.5 rounded-full bg-accent/15 border border-accent/25 text-accent text-[10px] font-bold flex-shrink-0">
                  {unreadChat} msg
                </span>
              )}
            </Link>
          )
        })}
      </nav>

      {/* User + Logout */}
      <div className="px-3 pb-4 border-t border-white/[0.08] pt-4">
        <div className="px-3 py-2 mb-1.5 glass-card rounded-xl flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl overflow-hidden flex-shrink-0 border border-accent/20">
            {avatarBlobUrl ? (
              <img src={avatarBlobUrl} alt="Avatar" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-accent/20 flex items-center justify-center">
                <span className="text-accent text-xs font-bold">{initials || user.email[0].toUpperCase()}</span>
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-text-primary text-xs font-medium truncate">{user.email}</p>
            <p className="text-text-muted text-[10px] mt-0.5">
              Prestataire · {profileComplete === false ? (
                <span className="text-[#F59E0B]">Profil incomplet</span>
              ) : profileComplete === true ? (
                <span className="text-success">Profil complet</span>
              ) : 'Chargement…'}
            </p>
          </div>
        </div>
        <button
          onClick={onLogout}
          className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium w-full text-left text-text-secondary hover:bg-danger/10 hover:text-danger transition-all duration-200 border border-transparent"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
          Déconnexion
        </button>
      </div>
    </aside>
  )
}
